import React, { useCallback, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'

import clsx from 'clsx'
import { isEmpty } from 'lodash'
import { Box, Grid, IconButton, Typography } from '@material-ui/core'
import { ArrowLeft as ArrowLeftIcon } from 'react-feather'

import { ChatPanel, InventoryPanel } from './components'
import CancelRegistrationDialog from './components/CancelRegistrationDialog'

import DaiChatContext from 'contexts/DaiChatContext'

import useStyles from './styles'

// O agente quebra linhas com `\n` simples (ex.: listas com •). Sem isso o
// Markdown colapsaria essas quebras; convertê-las em hard break (`  \n`)
// preserva a formatação.
const toHardBreaks = (text) =>
  typeof text === 'string' ? text.replace(/\n/g, '  \n') : ''

// Adapta as mensagens do fluxo de inventário (role user/assistant, campo text)
// para o formato consumido pelos componentes do chat da DAI (role human/ai,
// campo content), marcando a última resposta como streaming enquanto envia.
const mapMessages = (messages, isSending) =>
  messages.map((message, index) => {
    const isAssistant = message.role === 'assistant'
    const isLast = index === messages.length - 1
    const streaming = isAssistant && isLast && isSending

    return {
      id: message.id,
      role: isAssistant ? 'ai' : 'human',
      content: isAssistant ? toHardBreaks(message.text) : message.text,
      streaming,
      phase: streaming && !message.text ? 'thinking' : null,
    }
  })

const AiInventoryDaiChat = ({
  title,
  messages,
  items,
  activeItemId,
  showConclusion,
  onConclude,
  onSend,
  onBack,
  isSending = false,
  abortMessage,
  fullBleed = false,
  showRisk = true,
  userName,
}) => {
  const classes = useStyles()

  const [inputMessage, setInputMessage] = useState('')
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const inputRef = useRef(null)
  const messagesContainerRef = useRef(null)
  const chatContainerRef = useRef(null)

  const mappedMessages = useMemo(
    () => mapMessages(messages, isSending),
    [messages, isSending],
  )

  // O card de listagem de IAs só aparece após a primeira IA cadastrada.
  const hasItems = !isEmpty(items)

  const handleSendMessage = useCallback(() => {
    const content = inputMessage.trim()
    if (!content) return
    onSend(content)
    setInputMessage('')
  }, [inputMessage, onSend])

  // Fornece o contexto esperado pelos componentes da DAI (ChatMessages e
  // ChatActions) a partir do estado do fluxo de inventário.
  const daiChatValue = useMemo(
    () => ({
      messages: mappedMessages,
      loading: false,
      isSending,
      userName,
      hasMessage: !isEmpty(mappedMessages),
      inputMessage,
      setInputMessage,
      inputRef,
      messagesContainerRef,
      chatContainerRef,
      sendMessage: handleSendMessage,
      abortMessage: abortMessage || (() => {}),
      daiChat: null,
      registerMixpanelEvent: () => {},
    }),
    [
      mappedMessages,
      isSending,
      inputMessage,
      handleSendMessage,
      abortMessage,
      userName,
    ],
  )

  return (
    <DaiChatContext.Provider value={daiChatValue}>
      <Box
        className={clsx(classes.wrapper, {
          [classes.fullBleed]: fullBleed,
          [classes.appSpacing]: !fullBleed,
        })}
      >
        {/* Cabeçalho da tela (texto + voltar), no padrão do projeto de
            governança. O voltar confirma antes de descartar a conversa. */}
        <Box className={classes.screenHeader}>
          <IconButton
            aria-label="Voltar"
            onClick={() => setCancelDialogOpen(true)}
          >
            <ArrowLeftIcon />
          </IconButton>
          <Typography variant="h5" className={classes.screenTitle}>
            {title}
          </Typography>
        </Box>

        <Grid
          container
          spacing={3}
          className={clsx(
            classes.gridBase,
            fullBleed ? classes.gridFullBleed : classes.gridApp,
          )}
        >
          {/* O painel de listagem só surge depois que a primeira IA é
              cadastrada; até lá o chat ocupa a largura inteira. */}
          <Grid item xs={12} md={hasItems ? 8 : 12} className={classes.column}>
            <ChatPanel />
          </Grid>
          {hasItems && (
            <Grid item xs={12} md={4} className={classes.column}>
              <InventoryPanel
                items={items}
                activeItemId={activeItemId}
                showConclusion={showConclusion}
                onConclude={onConclude}
                showRisk={showRisk}
              />
            </Grid>
          )}
        </Grid>
      </Box>

      <CancelRegistrationDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={onBack}
      />
    </DaiChatContext.Provider>
  )
}

AiInventoryDaiChat.propTypes = {
  title: PropTypes.string.isRequired,
  messages: PropTypes.array.isRequired,
  items: PropTypes.array,
  activeItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showConclusion: PropTypes.bool,
  onConclude: PropTypes.func,
  onSend: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  isSending: PropTypes.bool,
  abortMessage: PropTypes.func,
  fullBleed: PropTypes.bool,
  showRisk: PropTypes.bool,
  userName: PropTypes.string,
}

export default AiInventoryDaiChat
